import ApiService from './api.service';
import type { ListRecordsResponse, SingleRecordResponse } from './pipelineNode.service';

export type { ListRecordsResponse };

export interface ArtifactComment {
  id: string;
  user_id: string;
  text: string;
  created_at: string;
  updated_at: string | null;
}

export interface EllieArtifact {
  id: string;
  user_id: string;
  created_at: string;
  name: string;
  description: string;
  markdown: string;
  comments: ArtifactComment[];
  agent_id: string | null;
  agent_name: string | null;
  shared_with: string[];
  shared_with_all: boolean;
}

export class EllieArtifactService {
  async find(options?: {
    page?: number;
    pageSize?: number;
  }): Promise<ListRecordsResponse<EllieArtifact>> {
    const {
      page = 1,
      pageSize = 50,
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
    };

    const result = await ApiService.getInstance().request(
      'GET',
      '/ellieartifacts',
      queryParams
    ) as ListRecordsResponse<EllieArtifact>;

    return result;
  }

  async findById(id: string): Promise<EllieArtifact> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/ellieartifacts/${id}`
    ) as SingleRecordResponse<EllieArtifact>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/ellieartifacts/${id}`
    );
  }

  async listComments(artifactId: string): Promise<ArtifactComment[]> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/ellieartifacts/${artifactId}/comments`
    ) as { comments: ArtifactComment[] };
    return result.comments;
  }

  async addComment(artifactId: string, text: string): Promise<ArtifactComment> {
    const result = await ApiService.getInstance().request(
      'POST',
      `/ellieartifacts/${artifactId}/comments`,
      { text }
    ) as { comment: ArtifactComment };
    return result.comment;
  }

  async updateComment(artifactId: string, commentId: string, text: string): Promise<ArtifactComment> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/ellieartifacts/${artifactId}/comments/${commentId}`,
      { text }
    ) as { comment: ArtifactComment };
    return result.comment;
  }

  async deleteComment(artifactId: string, commentId: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/ellieartifacts/${artifactId}/comments/${commentId}`
    );
  }

  async updateSharing(artifactId: string, sharedWith: string[], sharedWithAll: boolean): Promise<EllieArtifact> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/ellieartifacts/${artifactId}/sharing`,
      { shared_with: sharedWith, shared_with_all: sharedWithAll }
    ) as SingleRecordResponse<EllieArtifact>;
    return result.record;
  }
}

export const ellieArtifactService = new EllieArtifactService();
